import { useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, BadgeCheck, Star } from 'lucide-react';

import { useStoreQuery } from '@/client/api/stores/stores.query';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { StoreStatusBadge } from './StoreList/StoreStatusBadge';
import { OverviewTab } from './StoreDetail/OverviewTab';
import { ModerationTab } from './StoreDetail/ModerationTab';
import { PlanTab } from './StoreDetail/PlanTab';
import { PayoutTab } from './StoreDetail/PayoutTab';
import { SettingsTab } from './StoreDetail/SettingsTab';
import { AuditTab } from './StoreDetail/AuditTab';
import { PerformanceTab } from './StoreDetail/PerformanceTab';
import { InventoryTab } from './StoreDetail/InventoryTab';
import { FulfilmentTab } from './StoreDetail/FulfilmentTab';
import { TransactionsTab } from './StoreDetail/TransactionsTab';
import { OrdersTab } from './StoreDetail/OrdersTab';
import { ListingsTab } from './StoreDetail/ListingsTab';
import { StaffTab } from './StoreDetail/StaffTab';
import { RefundsTab } from './StoreDetail/RefundsTab';
import { PricingTab } from './StoreDetail/PricingTab';
import { VouchersTab } from './StoreDetail/VouchersTab';
import { BillingTab } from './StoreDetail/BillingTab';
import { MessagesTab } from './StoreDetail/MessagesTab';
import { StoreManagerMobileNav, StoreManagerSidebar, StoreManagerSidebarSkeleton, titleForTab } from './StoreDetail/StoreManagerShell';
import { SalesChartSkeleton } from './StoreDetail/SalesChart';
import { TableSkeleton } from './StoreDetail/shared';
import { storeLogoUrl } from './storeMedia';

/** Mirrors the seller's Store Manager, then the admin-only tabs. */
const TABS = [
  'Overview', 'Performance', 'Orders', 'Refunds', 'Fulfilment',
  'Inventory', 'Pricing', 'Listings', 'Transactions', 'Payout',
  'Vouchers', 'Billing', 'Staff', 'Messages', 'Settings',
  'Plan', 'Moderation', 'Audit',
] as const;
type Tab = (typeof TABS)[number];

const isTab = (v: string | null): v is Tab => v != null && (TABS as readonly string[]).includes(v);

const StoreDetailPage = () => {
  const { storeid: storeidParam } = useParams();
  const storeid = Number(storeidParam);
  const navigate = useNavigate();
  // The tab lives in the URL (?tab=Orders) so support can share a deep link.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const tab: Tab = isTab(tabParam) ? tabParam : 'Overview';
  const setTab = (t: Tab) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (t === 'Overview') next.delete('tab');
        else next.set('tab', t);
        return next;
      },
      { replace: true },
    );

  const { data: store, isLoading, notFound } = useStoreQuery(
    Number.isFinite(storeid) ? storeid : null,
  );

  useBreadcrumbs(
    useMemo(
      () => [
        { label: 'Stores', href: '/admin/stores' },
        { label: store?.name ?? 'Store' },
      ],
      [store?.name],
    ),
  );

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#f1f2f6] px-4 py-6 sm:px-6">
        <div className="mx-auto w-full max-w-[1320px]">
          <Button variant="ghost" onClick={() => navigate('/admin/stores')} className="mb-4 gap-1.5">
            <ArrowLeft className="h-4 w-4" /> Back to stores
          </Button>
          <div className="rounded-xl bg-white p-12 text-center text-gray-500 shadow-sm ring-1 ring-gray-200/60">
            Store #{storeidParam} was not found.
          </div>
        </div>
      </div>
    );
  }

  const logo = store ? storeLogoUrl(store) : undefined;

  return (
    <div className="min-h-screen bg-[#f1f2f6] px-4 py-6 sm:px-6">
      {/* Store Manager — centred, capped width on large screens (matches ff-ui). */}
      <div className="mx-auto w-full max-w-[1320px]">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="border-l-2 border-blue-500 pl-3 text-2xl font-semibold text-gray-500">Store Manager</h1>
          <Button variant="ghost" onClick={() => navigate('/admin/stores')} className="gap-1.5 text-gray-600">
            <ArrowLeft className="h-4 w-4" /> All stores
          </Button>
        </div>

        {isLoading || !store ? (
          <div className="grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
            <aside className="hidden lg:block">
              <StoreManagerSidebarSkeleton />
            </aside>
            <main className="min-w-0 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200/60 sm:p-6" aria-busy="true">
              <Skeleton className="mb-4 h-10 w-full rounded-lg lg:hidden" />
              <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <Skeleton className="h-7 w-40" />
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-lg" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-36" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
              </header>
              {tab === 'Overview' || tab === 'Performance' ? <SalesChartSkeleton /> : <TableSkeleton />}
            </main>
          </div>
        ) : (
          <div className="grid items-start gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
            <aside className="hidden lg:sticky lg:top-6 lg:block">
              <StoreManagerSidebar store={store} tab={tab} onSelect={(t) => isTab(t) && setTab(t)} />
            </aside>

            <main className="min-w-0 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200/60 sm:p-6">
              <div className="mb-4 lg:hidden">
                <StoreManagerMobileNav tab={tab} onSelect={(t) => isTab(t) && setTab(t)} />
              </div>

              <header className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <h2 className="text-xl font-semibold text-gray-900">{titleForTab(tab)}</h2>
                <div className="flex min-w-0 items-center gap-3 rounded-lg px-2 py-1">
                  <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-blue-50 text-sm font-semibold text-blue-700">
                    {logo ? <img src={logo} alt="" className="h-full w-full object-cover" /> : store.name.charAt(0)}
                  </span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-semibold text-gray-900">{store.name}</span>
                      {store.verified === 1 && <BadgeCheck className="h-3.5 w-3.5 flex-shrink-0 text-blue-500" aria-label="Verified" />}
                      {store.featured === 1 && <Star className="h-3.5 w-3.5 flex-shrink-0 text-purple-500" aria-label="Featured" />}
                    </span>
                    <span className="block truncate text-xs text-gray-500">
                      {store.owner?.name ?? (store.oid != null ? `Org #${store.oid}` : `User #${store.uid ?? '—'}`)} · #{store.storeid}
                    </span>
                  </span>
                  <StoreStatusBadge store={store} />
                </div>
              </header>

          {tab === 'Overview' && <OverviewTab store={store} onNavigate={(t) => isTab(t) && setTab(t)} />}
          {tab === 'Performance' && <PerformanceTab store={store} />}
          {tab === 'Inventory' && <InventoryTab store={store} />}
          {tab === 'Fulfilment' && <FulfilmentTab store={store} />}
          {tab === 'Transactions' && <TransactionsTab store={store} />}
          {tab === 'Moderation' && <ModerationTab store={store} onOpenAudit={() => setTab('Audit')} />}
          {tab === 'Plan' && <PlanTab store={store} />}
          {tab === 'Payout' && <PayoutTab store={store} />}
          {tab === 'Settings' && <SettingsTab store={store} onGoToModeration={() => setTab('Moderation')} />}
          {tab === 'Audit' && <AuditTab store={store} />}
          {tab === 'Listings' && <ListingsTab store={store} />}
          {tab === 'Staff' && <StaffTab store={store} />}
          {tab === 'Orders' && <OrdersTab store={store} />}
          {tab === 'Refunds' && <RefundsTab store={store} />}
          {tab === 'Pricing' && <PricingTab store={store} />}
          {tab === 'Vouchers' && <VouchersTab store={store} />}
          {tab === 'Billing' && <BillingTab store={store} />}
          {tab === 'Messages' && <MessagesTab store={store} />}
            </main>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreDetailPage;
